import { configureStore, combineReducers } from '@reduxjs/toolkit'
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux'
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist'
// redux-persist/lib/storage 是 CJS 模块（__esModule + exports.default），
// Vite 8 Rolldown 的 CJS 互操作未正确解析 default export，直接构造等价的 storage 对象。
// 等价于 redux-persist 的 createWebStorage('local')，包装 localStorage 为 Promise API。
const storage = {
  getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
  setItem: (key: string, value: any) => Promise.resolve(localStorage.setItem(key, value)),
  removeItem: (key: string) => Promise.resolve(localStorage.removeItem(key)),
}

// 导入所有切片
import notesSlice from './slices/notesSlice'
import editorSlice from './slices/editorSlice'
import uiSlice from './slices/uiSlice'
import syncSlice from './slices/syncSlice'
import blockCacheSlice from './slices/blockCacheSlice'
import paragraphBlockSyncSlice from './slices/paragraphBlockSyncSlice'
import modeSlice from './slices/modeSlice'
import standardNoteEditorSlice from './slices/standardNoteEditorSlice'
import sujiNoteEditorSlice from './slices/sujiNoteEditorSlice'
import rightEditorSlice from './slices/rightEditorSlice'
import leftEditorSlice from './slices/leftEditorSlice'
import crdtMultiNoteEditorSlice from './slices/crdtMultiNoteEditorSlice'
import sujiCRDTMultiNoteEditorSlice from './slices/sujiCRDTMultiNoteEditorSlice'
import lockSessionSlice from './slices/lockSessionSlice'
import quotaSlice from './slices/quotaSlice'
import noteGroupSlice from './slices/noteGroupSlice'
import promptSidebarSlice from './slices/promptSidebarSlice'
import editorPositionsSlice from './slices/editorPositionsSlice'
import saveIndicatorSlice from './slices/saveIndicatorSlice'

// 使用Redux Toolkit的configureStore创建静态store
// 由于redux-dynamic-modules API使用问题，暂时使用静态配置
// 后续可以升级到正确的动态模块实现

// 组合reducer
const rootReducer = combineReducers({
  notes: notesSlice,
  editor: editorSlice,
  ui: uiSlice,
  sync: syncSlice,
  blockCache: blockCacheSlice,
  paragraphBlockSync: paragraphBlockSyncSlice,
  mode: modeSlice,
  // 直接导入两种模式的切片，静态配置
  multiNoteEditor: standardNoteEditorSlice, // 保留旧的slice名称以兼容现有代码
  standardNoteEditor: standardNoteEditorSlice,
  sujiNoteEditor: sujiNoteEditorSlice,
  rightEditor: rightEditorSlice,
  leftEditor: leftEditorSlice,
  crdtMultiNoteEditor: crdtMultiNoteEditorSlice,
  sujiCRDTMultiNoteEditor: sujiCRDTMultiNoteEditorSlice,
  lockSession: lockSessionSlice,
  quota: quotaSlice,
  noteGroups: noteGroupSlice,
  promptSidebar: promptSidebarSlice,
  editorPositions: editorPositionsSlice,
  saveIndicator: saveIndicatorSlice,
})

// Redux持久化配置
const persistConfig: import('redux-persist').PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'root',
  version: 1,
  storage,
  // 只持久化编辑器相关的slice，避免持久化不必要的数据
  whitelist: [
    'crdtMultiNoteEditor',
    'sujiCRDTMultiNoteEditor'
  ],
}

// 创建持久化reducer
const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredPaths: ['register']
      }
    }),
  devTools: process.env.NODE_ENV !== 'production'
})

// 创建持久化store
export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export default store